/**
 * RFC 5545 (iCalendar) export and import.
 *
 * Writing is lossy by design: attachments and recurrence rules cannot round
 * trip through this app, so VEVENTs are flattened to single occurrences and an
 * in-app toast says how many were read. Text is folded and escaped per the
 * spec so exported files open cleanly in Outlook, Google Calendar and Apple
 * Calendar.
 */

import {
  addDays,
  civilToInstant,
  instantToCivil,
  type CalendarEvent,
  type EventStatus,
} from "@calendar/shared";

export interface IcsEvent {
  title: string;
  notes: string;
  location: string;
  startUtc: string;
  endUtc: string | null;
  allDay: boolean;
  status: EventStatus;
}

interface RawProp {
  params: Map<string, string>;
  value: string;
}

const CRLF = "\r\n";

function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,");
}

function unescapeText(value: string): string {
  return value
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

/** "2026-09-30T06:30:00.000Z" -> "20260930T063000Z". */
function utcStamp(iso: string): string {
  return new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

/** {2026,9,30} -> "20260930". Dates are always written in the Gregorian frame. */
function dateStamp(date: { year: number; month: number; day: number }): string {
  return `${String(date.year).padStart(4, "0")}${String(date.month).padStart(2, "0")}${String(date.day).padStart(2, "0")}`;
}

/** Fold a line at 75 octets; continuation lines begin with a space. */
function fold(line: string): string {
  const out: string[] = [];
  let rest = line;
  while (rest.length > 75) {
    out.push(rest.slice(0, 75));
    rest = " " + rest.slice(75);
  }
  out.push(rest);
  return out.join(CRLF);
}

function toStatus(event: CalendarEvent): string {
  switch (event.status) {
    case "tentative":
      return "TENTATIVE";
    case "cancelled":
      return "CANCELLED";
    default:
      return "CONFIRMED";
  }
}

/** Serialise events into a single VCALENDAR document. */
export function eventsToIcs(events: CalendarEvent[]): string {
  const out: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Smart Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];
  const stamp = utcStamp(new Date().toISOString());

  for (const event of events) {
    out.push("BEGIN:VEVENT", `UID:${event.id}`, `DTSTAMP:${stamp}`);
    if (event.allDay) {
      const start = instantToCivil(event.startUtc, event.timeZone, "gregorian").date;
      // RFC 5545 all-day ends are exclusive, so a single-day event must claim a
      // whole extra day to stay visible in consumer calendars.
      const end =
        event.endUtc !== event.startUtc
          ? instantToCivil(event.endUtc, event.timeZone, "gregorian").date
          : addDays(start, 1, "gregorian");
      out.push(
        `DTSTART;VALUE=DATE:${dateStamp(start)}`,
        `DTEND;VALUE=DATE:${dateStamp(end)}`,
      );
    } else {
      out.push(`DTSTART:${utcStamp(event.startUtc)}`, `DTEND:${utcStamp(event.endUtc)}`);
    }
    const title = event.title.trim() || "Event";
    out.push(`SUMMARY:${escapeText(title)}`);
    if (event.location) out.push(`LOCATION:${escapeText(event.location)}`);
    if (event.notes) out.push(`DESCRIPTION:${escapeText(event.notes)}`);
    out.push(`STATUS:${toStatus(event)}`);
    out.push("END:VEVENT");
  }

  out.push("END:VCALENDAR");
  return out.map(fold).join(CRLF) + CRLF;
}

/**
 * DTSTART/DTEND parsing. Returns either a real UTC instant or the floating
 * civil stamp (Gregorian) that still needs a time zone before use.
 */
function parseStamp(value: string): { iso: string; floating: boolean } {
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/.exec(value.trim());
  if (!m) throw new Error("Bad ICS date: " + value);
  const [, y, mo, d, h = "0", mi = "0", s = "0", z] = m;
  if (z) {
    return {
      iso: new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s)))
        .toISOString(),
      floating: false,
    };
  }
  return {
    iso: `${Number(mo)}/${Number(d)}/${Number(y)} ${Number(h)}:${Number(mi)}:${Number(s)}`,
    floating: true,
  };
}

/** "2026/9/30 6:30:0" -> {date,hour,minute} in the Gregorian frame. */
function civilFromFloating(iso: string): { date: { year: number; month: number; day: number }; hour: number; minute: number } {
  const [datePart, timePart] = iso.split(" ");
  const [mo, d, y] = datePart.split("/").map(Number);
  const [h = 0, mi = 0] = (timePart ?? "").split(":").map((p) => Number(p) || 0);
  return { date: { year: y, month: mo, day: d }, hour: h, minute: mi };
}

/** Parse a VCALENDAR slice of an ICS document into plain events. */
export function parseIcs(text: string, timeZone: string): IcsEvent[] {
  // Unfold continuation lines (leading space = previous line continues).
  const logical: string[] = [];
  for (const raw of text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n")) {
    if ((raw.startsWith(" ") || raw.startsWith("\t")) && logical.length) {
      logical[logical.length - 1] += raw.slice(1);
    } else if (raw.trim() !== "") {
      logical.push(raw.trim());
    }
  }

  // Collect each BEGIN:VEVENT ... END:VEVENT body.
  const blocks: string[][] = [];
  let current: string[] | null = null;
  for (const line of logical) {
    if (line.startsWith("BEGIN:VEVENT")) {
      current = [];
      continue;
    }
    if (line.startsWith("END:VEVENT")) {
      if (current) blocks.push(current);
      current = null;
      continue;
    }
    if (current) current.push(line);
  }

  const events: IcsEvent[] = [];
  for (const block of blocks) {
    const prop = new Map<string, RawProp>();
    for (const line of block) {
      const colon = line.indexOf(":");
      if (colon < 0) continue;
      const header = line.slice(0, colon);
      const semi = header.indexOf(";");
      const name = (semi < 0 ? header : header.slice(0, semi)).toUpperCase();
      const params = new Map<string, string>();
      if (semi >= 0) {
        for (const part of header.slice(semi + 1).split(";")) {
          const eq = part.indexOf("=");
          if (eq > 0) params.set(part.slice(0, eq).toUpperCase(), part.slice(eq + 1));
        }
      }
      prop.set(name, { params, value: line.slice(colon + 1) });
    }
    const event = toEvent(prop, timeZone);
    if (event) events.push(event);
  }
  return events;
}

function toEvent(prop: Map<string, RawProp>, timeZone: string): IcsEvent | null {
  const get = (name: string) => (prop.get(name)?.value ? unescapeText(prop.get(name)!.value) : "");
  const start = prop.get("DTSTART");
  if (!start) return null;
  const allDay = start.params.get("VALUE") === "DATE";
  const startParsed = parseStamp(start.value);
  const tz = start.params.get("TZID") ?? (startParsed.floating ? timeZone : "UTC");
  const toUtc = (parsed: { iso: string; floating: boolean }): string => {
    if (!parsed.floating) return parsed.iso;
    const civil = civilFromFloating(parsed.iso);
    return civilToInstant(civil.date, civil.hour, civil.minute, tz, "gregorian");
  };

  const startUtc = toUtc(startParsed);
  const endProp = prop.get("DTEND");
  let endUtc: string | null = null;
  if (endProp) {
    try {
      endUtc = toUtc(parseStamp(endProp.value));
    } catch {
      endUtc = null;
    }
  }

  return {
    title: (get("SUMMARY") ?? "").trim() || "Imported event",
    notes: get("DESCRIPTION") ?? "",
    location: get("LOCATION") ?? "",
    startUtc,
    endUtc,
    allDay,
    status: (get("STATUS") ?? "").toUpperCase() === "CANCELLED" ? "cancelled" : "confirmed",
  };
}